/* oxlint-disable typescript/no-floating-promises */
/* oxlint-disable no-console */

import html2canvas from 'html2canvas';

import { ErrorCode, ErrorMessage } from '@biteinc/common';
import { ApiHeader, BiteLogType, ClientApiVersion, FlashBridgeMessage } from '@biteinc/enums';

import { useStore } from '~/stores';

import { ApiService } from './gcn_maitred_request_manager';

declare global {
  interface Window {
    bridgeIsReady: boolean;
  }
}

export const getKioskPreviewBridge = ({
  gcnBuildName,
  authToken,
  showCover,
}: {
  gcnBuildName: string;
  authToken: string;
  showCover: () => void;
}): Bridge => {
  const apiVersion = ClientApiVersion.SupportsErrorMessage;

  function getHostFromApiService(apiService: ApiService): string {
    switch (apiService) {
      case ApiService.Flash:
      case ApiService.LogsApiV1:
      case ApiService.LoyaltyApiV1:
      case ApiService.LoyaltyApiV2:
      case ApiService.Maitred:
      case ApiService.OrdersApiV1:
      case ApiService.OrdersApiV2:
      case ApiService.PaymentsApiV2:
      case ApiService.RecommendationsApi:
        return '';
    }
  }

  // promised async method
  const browserRequest = async (
    apiService: ApiService,
    type: 'GET' | 'POST',
    path: string,
    data: any,
    timeout: number,
  ): Promise<any> => {
    const location = useStore.getState().bridge.location;
    const abortController = new AbortController();
    const url = `${getHostFromApiService(apiService)}${path}`;

    console.log(`browserRequest -> ${type} ${url}`, data);

    setTimeout(() => {
      abortController.abort();
    }, timeout);

    const res = await fetch(url, {
      signal: abortController.signal,
      method: type,
      body: type.toLowerCase() === 'get' ? undefined : JSON.stringify(data),
      headers: {
        [ApiHeader.ApiVersion]: `${apiVersion}`,
        ...(authToken && {
          Authorization: `Bearer ${authToken}`,
        }),
        [ApiHeader.LocationId]: location._id,
        [ApiHeader.OrgId]: location.orgId,
        ...(type.toLowerCase() === 'get' ? undefined : { 'content-type': 'application/json' }),
        [ApiHeader.GcnBuildName]: gcnBuildName,
      },
    }).catch((_err) => {
      // We can assume that an error here is going to be due to the network
      throw {
        success: false,
        error: {
          code: ErrorCode.NetworkRequestTimedOut,
          message: ErrorMessage.EXTERNAL_NETWORK,
        },
      };
    });

    if (res.ok) {
      const json = await res.json();
      console.log(`request <- ${res.status} ${type} ${path}`, JSON.stringify(json));
      return json;
    }

    const errorJson = await res.json();
    console.log(`request <- ${res.status} ${type} ${path}`, JSON.stringify(errorJson));
    throw {
      success: false,
      error: errorJson,
    };
  };

  // instead of using mongoId or bson to generate ids, just randomly generate
  const pseudoObjectId = (): string => {
    const hexRadix = 16;
    const toHex = (value: number): string => {
      return Math.floor(value).toString(hexRadix);
    };
    return (
      toHex(Date.now() / 1000) +
      ' '.repeat(hexRadix).replace(/./g, () => {
        return toHex(Math.random() * hexRadix);
      })
    );
  };

  const sendLog = async (data: any, type: BiteLogType = BiteLogType.Device): Promise<void> => {
    const location = useStore.getState().bridge.location;
    const locationId = location._id;
    const orgId = location.orgId;

    const logJson = {
      ...data,
      type,
      clientId: pseudoObjectId(),
      clientName: 'kiosk-preview',
      createdAt: Date.now(),
    };
    await browserRequest(
      ApiService.LogsApiV1,
      'POST',
      `/api/orgs/${orgId}/locations/${locationId}/logs`,
      logJson,
      2000,
    );
  };

  // create a transaction
  const demoPayTransactionWithAmount = (amount: number): any => {
    const location = useStore.getState().bridge.location;
    const locationId = location._id;
    const orgId = location.orgId;
    const maskPan = '123456******9876';
    const now = Date.now();
    const clientId = pseudoObjectId();
    const authCode = `${Math.floor(Math.random() * 899999) + 100000}`;
    const paymentsApiVersion = 'V2';

    const transactionJson: Record<string, any> = {
      amount,
      authCode,
      clientId,
      createdAt: now,
      finishedAt: now,
      // TODO: get from constants
      gateway: 0, // Gateway.None,
      gatewaySoftwareVersion: 'DEMO_PAY',
      last4: maskPan.slice(-4),
      locationId,
      maskPan,
      orgId,
      cardSchemeId: 2, // CardSchemeId.MasterCard,
      result: 1, // TransactionResult.Approved,
      startedAt: now,
      state: 2, // TransactionState.Committed,
      transactionTime: now,
      type: 0, // TransactionType.Sale,
      updatedAt: now,
      paymentsApiVersion, // PaymentsApiVersion
      userAction: 0,
      amountAuthorized: amount,
      currencyCode: 'USD', // Currency.USD
    };

    return transactionJson;
  };

  // browser bridge
  return {
    init: () => {
      // stub
      console.log('browser bridge init');
      window.bridgeIsReady = true;
    },
    send: async (payload: any, callback: any) => {
      switch (payload.event) {
        case FlashBridgeMessage.GO_HOME: {
          showCover();
          break;
        }
        case 'ready':
          // dunno what to do with these yet
          break;
        case FlashBridgeMessage.IMAGE:
          console.log('kiosk-preview-bridge -> loading image', payload);
          return payload.imageUrl;
        case FlashBridgeMessage.MAITRED_REQUEST:
          {
            if (!payload.request) {
              console.log('BAD maitredRequest', JSON.stringify(payload));
              break;
            }
            const { apiService, path, body, timeout, method, requestId } = payload.request;

            console.log('capture maitredRequest event', {
              apiService,
              method,
              path,
              timeout,
              requestId,
            });
            console.log('request payload', JSON.stringify(body));
            browserRequest(apiService, method, path, body, timeout)
              .then((response) => {
                callback(response);
              })
              .catch((err) => {
                callback(err);
              });
          }
          break;
        case 'makePayment':
          callback(demoPayTransactionWithAmount(payload.amountInCents));
          break;
        case 'printReceipt':
          window.top?.postMessage(
            { type: 'kiosk-preview::print', payload: payload.printPayload.printJobs },
            '*',
          );
          callback({});
          break;
        case 'userDidRequestToPrintReceipt':
          callback({});
          break;
        case 'orderCompleted': {
          const location = useStore.getState().bridge.location;

          browserRequest(
            ApiService.Maitred,
            'POST',
            `/api/orgs/${location.orgId}/locations/${location._id}/guests`,
            {
              _id: payload.guestId,
            },
            2000,
          );
          break;
        }
        case FlashBridgeMessage.DEVICE_GAZEBO_LOG: {
          console.log(FlashBridgeMessage.DEVICE_GAZEBO_LOG, payload);
          if (payload.takeScreenshot) {
            try {
              const canvas = await html2canvas(document.body);
              payload.screenshotData = canvas
                .toDataURL('image/jpeg', 0.8)
                // native clients don't include data content types in their base64 data,
                // so strip it out for consistency
                .replace('data:image/jpeg;base64,', '');
            } catch {
              console.error('Failed to get a screenshot');
            }
          }
          await sendLog(payload);
          break;
        }
        case FlashBridgeMessage.ENABLE_REDUCED_VIEWPORT_HEIGHT: {
          window.top?.postMessage({ type: 'kiosk-preview::reduced-height-enable' }, '*');
          break;
        }
        case FlashBridgeMessage.DISABLE_REDUCED_VIEWPORT_HEIGHT: {
          window.top?.postMessage({ type: 'kiosk-preview::reduced-height-disable' }, '*');
          break;
        }
        case FlashBridgeMessage.GET_LRS_TABLE_TRACKER_GATEWAY_IP_ADDRESS: {
          // hard-coded response for the purposes of testing
          callback({ success: true, response: '10.0.1.145' });
          // callback({ success: false, response: 'Too many monkeys' });
          break;
        }
        default:
          console.log('unknown bridge message event', JSON.stringify(payload));
      }
    },

    async sendAsync(data: any) {
      return new Promise((resolve) => {
        this.send(data, (response: any) => {
          resolve(response);
        });
      });
    },
    sendLog: () => {},
  };
};
